import { useUpdateConnectedToMutation } from '@/apollo/gql/generated/graphql';
import { events } from '@/config/log_events';
import { useLoggingContext } from '@/contexts/useLogging';
import { useViewerContext } from '@/contexts/useViewer';
import { roleSelector } from '@/recoil/atom/userStatus';
import { customBugsnagLogger } from '@/utils/bugsnag';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';

const useConnectedTo = () => {
	const [updateConnectedTo] = useUpdateConnectedToMutation();
	const userRole = useRecoilValue(roleSelector);
	const router = useRouter();
	const slug = useMemo(() => router?.query?.id, [router]);
	const { viewer } = useViewerContext();
	const { containerIp, logToLambda } = useLoggingContext();

	const setConnected = async (
		userId: string,
		connected: boolean,
		id?: any,
		userType?: 'student' | 'instructor'
	) => {
		const event = connected
			? events.SENDING_CONNECT
			: events.SENDING_DISCONNECT;
		logToLambda(event, {
			user: viewer,
			metadata: {
				toUserId: userId,
				userType,
				as: userRole,
			},
		});

		try {
			await updateConnectedTo({
				variables: {
					input: {
						userId: viewer?.id,
						connectedTo: userId,
						classSlug: slug as string,
						connected,
						ghost: userRole === 'ghost' || userRole === 'super-ghost',
					},
				},
				context: {
					headers: {
						containerAddress: containerIp,
					},
				},
			});

			if (id && userType) {
				customBugsnagLogger.debug(
					`Instructor: ${viewer?.id} connected to ${userType}: ${id} at ${new Date().toISOString()} `
				);
			}
		} catch (error) {
			if (id && userType) {
				customBugsnagLogger.debug(
					`Instructor: Failed to connect to ${userType}`
				);
			}
			customBugsnagLogger.error(error);
		}
	};

	return { setConnected };
};

export default useConnectedTo;
